
import React, { useState } from "react";
import { DashboardAnalytics } from "@/components/newsletter/DashboardAnalytics";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCampaigns } from "@/hooks/useCampaigns";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Button } from "@/components/ui/button";
import { Download, FileSpreadsheet, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { addDays, subDays } from "date-fns";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export default function EmailAnalyticsDashboard() {
  useDocumentTitle("Email Analytics");
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState("overview");
  const { campaigns, isLoading } = useCampaigns();
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date(),
  });

  const handleExportData = () => {
    toast.info("Data export will be available in Phase 4");
  };

  const handleDateRangeChange = (range: { from: Date; to: Date }) => {
    setDateRange(range);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <Alert variant="warning" className="bg-amber-50 text-amber-800 border-amber-300">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Development Mode</AlertTitle>
        <AlertDescription>
          This analytics dashboard is in development mode and displays simulated data for demonstration purposes. 
          No actual email tracking or analytics are being collected at this time.
        </AlertDescription>
      </Alert>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Email Analytics Dashboard</h1>
          <p className="text-slate-600">Track performance metrics and engagement for your email campaigns</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            onClick={handleExportData}
          >
            <FileSpreadsheet size={16} />
            <span>Export</span>
          </Button>
          <DateRangePicker
            value={dateRange}
            onValueChange={handleDateRangeChange}
          />
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-md font-medium">Campaign:</h2>
          <Select 
            value={selectedCampaignId} 
            onValueChange={setSelectedCampaignId}
          >
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="All Campaigns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={undefined}>All Campaigns</SelectItem>
              {campaigns.map(campaign => (
                <SelectItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <Tabs defaultValue="overview" className="space-y-4" onValueChange={setActiveTab} value={activeTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="detailed">Detailed Analysis</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Performance Overview</CardTitle>
              <CardDescription>
                High-level metrics for your email campaigns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DashboardAnalytics campaignId={selectedCampaignId} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="detailed" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Detailed Analytics</CardTitle>
              <CardDescription>
                Comprehensive analysis of campaign performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DashboardAnalytics campaignId={selectedCampaignId} detailed={true} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
